/**
 * Las fotos de la pantalla de entrada y la frase que acompaña a cada una.
 *
 * Viven en R2 (`assets.1conecta.com/login/`, el mismo bucket público que usan
 * las portadas del blog) y **no en el repo**: la única foto que había antes
 * pesaba 840 KB de PNG y se bajaba en cada visita al login.
 *
 * **Son personas trabajando, no arte abstracto.** El primer set eran renders
 * pastel: bonitos, intercambiables con los de cualquier SaaS, y sus fondos
 * iban de durazno a lila sin pasar nunca por el azul de 1Conecta — el login
 * cambiaba de identidad cada nueve segundos. Una escena de trabajo dice qué es
 * esto; un degradado no dice nada.
 *
 * `lqip` es la misma foto a 32 px de ancho, en base64. A ese tamaño la imagen
 * ya no se reconoce pero sus colores y su composición sí, que es todo lo que
 * un fondo difuminado necesita. Va incrustada en el bundle a propósito: es lo
 * que hace que el fondo esté puesto en el primer pintado, en vez de dejar un
 * rectángulo gris mientras baja la foto.
 *
 * **Se bajan a 1400 px y no a 1920**: el panel mide unos 470 px y el fondo va
 * desenfocado a 95 px, así que el detalle de más no se ve en ninguno de los
 * dos lugares — y una foto real pesa mucho más que un degradado (las cinco
 * abstractas sumaban 430 KB; estas, a 1920, sumaban 1,58 MB).
 *
 * Fotos de Unsplash (licencia libre, sin atribución obligatoria). Para cambiar
 * una: `scripts/subir-fotos-login.cjs` la sube e imprime su url y su lqip.
 * Las claves son inmutables en caché, así que **una foto nueva va con nombre
 * nuevo** — por eso esta tanda empieza en 11 y no pisa a la anterior.
 */
export interface FondoLogin {
  url: string
  /** Miniatura de 32 px en base64; el fondo difuminado del primer pintado. */
  lqip: string
  /** Descripción para lectores de pantalla. */
  alt: string
  /** Frase que rota junto con la foto. */
  frase: string
  /**
   * Dónde está lo que hay que ver, como `object-position`. El panel es alto y
   * angosto, así que de una foto apaisada se recorta casi todo a los costados:
   * si el sujeto no está en el centro del original, el recorte lo parte al
   * medio. Es dato de cada foto y no una regla general — por eso vive acá y no
   * en el CSS. Sin valor, el centro.
   */
  foco?: string
}

const BASE = 'https://assets.1conecta.com/login'

export const FONDOS_LOGIN: FondoLogin[] = [
  {
    url: `${BASE}/11.webp`,
    lqip: 'data:image/webp;base64,UklGRrwBAABXRUJQVlA4ILABAABQCQCdASogADAAPsFIo0qnpCMhsBqqqPAYCWQAqSeDujXUKRxrFWx1xksI7ydKOdJy/nts0aJ7/xGsLxE425x/5gQ+ouVOC+k7A12oPONsHV/AAADMCl6tKho/SgzlHPcPlU7Ji3s53j4xdO1IyjJ5QOVfz/N70+EBzWov2uERil+K4Z5Nq5vg9tQx/35WDoS9QzHiXlcxi4yXt3oDcLtXlx4jWJhBgqe2QpL3lVmyeFX/v88FNQ//kjPHLhrEPqtScw0SxjvkV5GnOUy3nndbIcHeU2cDkvCphoILLhHygi9sUAm5KNq7HLW4FyHTHgmWV6knPZaUafl8Mqpmg8CKA8XSZBt7RnyLKkK7ISpYYxa/ayWFGsbEUxVBvvxyFag739u0uviRkksA235/COqUR8MGIJo/nsFxGq/qUwy8dGas0hJwofmwwoNhxCBvbxYEcZC2bn1lYU9/hMM3IfFuxzWJzUbIpFJSzopuvwh7B9LlPof0OQmGEMqwrbv7aEZPLX2dvTowYYZ9yM/roa0M94U4k/FAGbE+mF/bQ6jnEL6g7KjB3GqYFKnGlW3YAAA=',
    alt: 'Un equipo revisando un tablero en una oficina',
    frase: 'Tus clientes, tus números y tu equipo, al día.',
  },
  {
    url: `${BASE}/12.webp`,
    lqip: 'data:image/webp;base64,UklGRjgBAABXRUJQVlA4ICwBAADQBQCdASogABUAPsFOoEqnpCMhsAgA8BgJQBad7UEiA5RqBfoA3PdHoL1gNWegbWig5Wu3GNYAAP71dPsnLQ7ypghuQaSDniTbQ4SMTYnpY8g7neaZLumYlf/hK0PhVVdmuKPS0fNbOdeHQQfJo17OffQtiQNapvqSyZQKCZdeiAJoPicN2tK0ERfKoOa1HAIWqw8tZSef8XCrfCuTm7qoKNvrOS0AHP9zzQbD6hccfv5hKbOR60c7yqcSi/Mj5oS5XIF88QP0O5TSnidrjKkR/PMmTw+RHFm48M0W9kVPtB1mdt3653yvX3+ZRGQyEc4lJGK+Jr8OAgvhZzQ3810Y91WTNaS/nOQ7VEunaJMOT0wJz+gqWCUnSTlK/wFyZorRcZzO4OzD+ECAAAA=',
    alt: 'Tres personas trabajando sobre una laptop',
    frase: 'Todo tu negocio, en un solo lugar.',
  },
  {
    url: `${BASE}/13.webp`,
    lqip: 'data:image/webp;base64,UklGRsgAAABXRUJQVlA4ILwAAACQBQCdASogABUAPsFWpE4npCMiKAgA8BgJZQC7BoAB5H59bbJoWMmiMd65yu9vPN5ne6qsHAD2v1eLrT+RDw93RFN6CUajDMY4ewu/mq/c3B327p6lphpt05dyEsg/XYIi+nFRnsiL69Yd9Rm/+ObkB68Hzrs9U4zffrAGeGDrN7YpoNlecmKGm5Oug96BWsNtWOtQagW7R79boct0Xk3SS20RJzG+CD/+jJzRg6P99ibCCkzjCh4PEbRAAA==',
    alt: 'Una pizarra con los diagramas de un proyecto',
    frase: 'Lo que pasó, lo que falta y lo que viene.',
  },
  {
    url: `${BASE}/14.webp`,
    lqip: 'data:image/webp;base64,UklGRpwBAABXRUJQVlA4IJABAABQCACdASogADAAPsFUokynpKMiKrgMAPAYCWUAyNursn5XliA2jE/c/wywuDsXT9BO4cvqiBcnD+Qlt5X0FdPq0Nx09lGi09ux9AAA/vrkzqVFEtGYpMM6AfefVUUzPJZdUVZPrYmbdpz2b4WbNlpnggxzYq+wXGrDWeAx0iynQNKsDA4uRzjTxkgwgRP7p+U8kb/Iip1QBXz5tFYhfMbkv1rq6bwc/2GV50QMA4XLYTVg4LnuvBX0zGlPc0rK1C1O5Yjib+DJEK0Ctab2t1FZUt9QYZ2yK9XfQq1b1PSNsEK8RYQoEMjO8ZvRnkPIpB1bYIhl4Rz95x3FSrc8YpoqFc+Mu1ZFBM2brCOeqeC9QPOWrPZaaUAHVpO1/mxYLjghR+G9soniyf/T1Tai0MyH5VmuE/dlCRSz3bnZ3DYzHWGi1m1H53ob29wRI2cYTa+OmL8NhXQAlNPb2OsbMwXm+Bznlf9hZWpEFCws6edEabxvzax3FuJbU9g6rP4hEzV42OIdK4X4yUkghSmJAAAA',
    alt: 'Alguien ordenando ideas en una pared de vidrio',
    frase: 'Del primer contacto a la factura pagada.',
  },
  {
    url: `${BASE}/15.webp`,
    lqip: 'data:image/webp;base64,UklGRrwAAABXRUJQVlA4ILAAAADwBACdASogABUAPsFQokunpKMhqA1Q8BgJYwDNAoGvqgiIlGvfQnxmWGxkNbidQQAA/vMaVm13uM9i+H4Lj3ojjMw8gF1D5ZvReUkaWh4wRdTN8DMEpjkR3N01ParnFuUFww+0E68ocgiegWHnQEOqPysK2yVv5MW8v7CfRENxVME/oRhY2Sk8EoHlTy4jfPSjfIVRqcXraT65SrvQ1ut8hq47cFiUEnlNOsHBl4gAAA==',
    alt: 'Alguien presentando frente a una pizarra',
    frase: 'Propuestas, proyectos y cobranza sin cambiar de ventana.',
    // Está sobre el borde derecho del original: con el recorte al centro, el
    // panel la parte al medio.
    foco: '88% center',
  },
]

/**
 * Con qué foto arranca la sesión. Al azar y no siempre la primera: si abrís el
 * ERP todos los días, ver la misma imagen cada mañana convierte el slideshow
 * en un fondo fijo.
 */
export function fondoInicial(): number {
  return Math.floor(Math.random() * FONDOS_LOGIN.length)
}
